import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LabelList, Cell,
} from "recharts";
import { PhoneOff, PhoneCall, MessageSquare, Percent } from "lucide-react";
import ReportFilterBar from "@/components/ops/ReportFilterBar";
import LoadingOverlay from "@/components/common/LoadingOverlay";
import { useSortableTable, SortableTh } from "@/lib/useSortableTable";

const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`);

function Kpi({ label, value, sub, icon: Icon, testId }) {
  return (
    <div className="glass-card p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
          <div className="kpi-num text-3xl mt-1 text-slate-900">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center">
          <Icon className="w-5 h-5 text-slate-700" />
        </div>
      </div>
    </div>
  );
}

export default function DispositionDeepDivePage() {
  const [params, setParams] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/ops/reports/disposition-deep-dive", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [params]);

  const { rows, thProps } = useSortableTable(data?.rows || [], {
    initialKey: "total_converted", initialDir: "desc",
    accessors: { disposition: (r) => (r.disposition || "").toLowerCase() },
  });

  // Top-10 for chart display
  const chartTop = useMemo(
    () => [...(data?.rows || [])]
      .sort((a, b) => b.calls - a.calls)
      .slice(0, 10)
      .map((r) => ({
        name: r.disposition,
        calls: r.calls,
        converted: r.total_converted,
        rate: r.call_to_conv_pct,
        contactable: r.is_contactable,
      })),
    [data]
  );

  const totals = data?.totals || {};
  const overallRate = totals.total_calls
    ? (totals.total_converted / totals.total_calls) * 100
    : 0;

  return (
    <div className="space-y-6" data-testid="disposition-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight">
            Disposition deep-dive
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Which dispositions actually convert? Volume × conversion-rate at a glance.
          </p>
        </div>
        <ReportFilterBar onChange={setParams} testIdPrefix="disp" />
      </div>

      <LoadingOverlay loading={loading} testId="disp-loading">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi
            label="Total calls"
            value={fmt(totals.total_calls)}
            sub={`${fmt(totals.distinct_dispositions)} distinct dispositions`}
            icon={PhoneCall}
            testId="disp-kpi-calls"
          />
          <Kpi
            label="Total conversions"
            value={fmt(totals.total_converted)}
            sub="Across all channels"
            icon={MessageSquare}
            testId="disp-kpi-conv"
          />
          <Kpi
            label="Overall conv rate"
            value={fmtPct(overallRate)}
            sub="Calls that led to a credit"
            icon={Percent}
            testId="disp-kpi-rate"
          />
          <Kpi
            label="Unreachable share"
            value={fmt((data?.rows || []).filter((r) => !r.is_contactable).reduce((s, r) => s + r.calls, 0))}
            sub="Excluded dispositions total"
            icon={PhoneOff}
            testId="disp-kpi-excluded"
          />
        </div>

        {/* Chart: calls vs converted by disposition (top 10 volume) */}
        <div className="glass-card p-5 mt-8" data-testid="disp-chart">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
            Top 10 dispositions by call volume
          </div>
          {chartTop.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">No calls in the selected window.</div>
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartTop} margin={{ top: 24, right: 30, left: 0, bottom: 30 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={60} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }}
                         tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v, k) => (k === "rate" ? [`${Number(v).toFixed(1)}%`, "Conv rate"] : [fmt(v), k === "calls" ? "Calls" : "Converted"])} />
                  <Bar yAxisId="left" dataKey="calls" fill="#0A84FF" radius={[6, 6, 0, 0]}>
                    {chartTop.map((e, i) => (
                      <Cell key={i} fill={e.contactable ? "#0A84FF" : "#94A3B8"} />
                    ))}
                  </Bar>
                  <Bar yAxisId="left" dataKey="converted" fill="#22C55E" radius={[6, 6, 0, 0]}>
                    <LabelList dataKey="rate" position="top"
                      formatter={(v) => (v > 0 ? `${Number(v).toFixed(1)}%` : "")}
                      style={{ fontSize: 10, fill: "#475569" }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="mt-2 text-[11px] text-slate-500">
            <span className="inline-block w-2 h-2 rounded-full bg-[#0A84FF] mr-1" /> Calls (contactable)
            <span className="inline-block w-2 h-2 rounded-full bg-slate-400 ml-3 mr-1" /> Calls (excluded)
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 ml-3 mr-1" /> Converted
            <span className="ml-3">Label on top of converted bar = call-to-conversion %</span>
          </div>
        </div>

        {/* Full table */}
        <div className="glass-card p-5 mt-8" data-testid="disp-table">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
            All dispositions
          </div>
          <div className="overflow-auto max-h-[60vh]">
            <table className="w-full text-sm">
              <thead data-sticky>
                <tr className="text-left text-xs uppercase border-b border-slate-100">
                  <SortableTh {...thProps("disposition", "left")}>Disposition</SortableTh>
                  <SortableTh {...thProps("calls")}>Calls</SortableTh>
                  <SortableTh {...thProps("share_of_calls_pct")}>Share</SortableTh>
                  <SortableTh {...thProps("unique_users")}>Users</SortableTh>
                  <SortableTh {...thProps("converted_call")}>Call conv</SortableTh>
                  <SortableTh {...thProps("converted_sms")}>SMS conv</SortableTh>
                  <SortableTh {...thProps("converted_wn")}>WN conv</SortableTh>
                  <SortableTh {...thProps("converted_invalid")}>Inv conv</SortableTh>
                  <SortableTh {...thProps("total_converted")}>Total conv</SortableTh>
                  <SortableTh {...thProps("call_to_conv_pct")}>Conv rate</SortableTh>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.disposition} className="border-b border-slate-50/70">
                    <td className="py-2 px-2 font-medium text-slate-800">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-2 ${r.is_contactable ? "bg-emerald-500" : "bg-slate-400"}`} />
                      {r.disposition}
                      {!r.is_contactable && (
                        <span className="ml-2 text-[9px] uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Excluded</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(r.calls)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtPct(r.share_of_calls_pct)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(r.unique_users)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(r.converted_call)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(r.converted_sms)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(r.converted_wn)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmt(r.converted_invalid)}</td>
                    <td className="py-2 px-2 text-right tabular-nums font-semibold text-slate-900">{fmt(r.total_converted)}</td>
                    <td className="py-2 px-2 text-right">
                      <span className={`px-1.5 py-0.5 rounded tabular-nums ${r.call_to_conv_pct >= 30 ? "bg-emerald-50 text-emerald-700" : r.call_to_conv_pct >= 10 ? "bg-amber-50 text-amber-700" : "bg-slate-50 text-slate-600"}`}>
                        {fmtPct(r.call_to_conv_pct)}
                      </span>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={10} className="py-4 text-center text-slate-500">No dispositions.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </LoadingOverlay>
    </div>
  );
}
